import { SignUp } from '@clerk/react-router';
import { useEffect } from 'react';
import { useLocation } from 'react-router';
import {
  clearPendingRole,
  peekPendingRole,
  roleFromQueryValue,
  setPendingRole,
} from '~/features/user/pendingRoleIntent';
import { hasNestedClerkHash } from '~/routing/clerkContinuation';
import {
  CLERK_REDIRECT_URL_PARAM,
  CLERK_SATELLITE_URL_PARAM,
  RETURN_TO_PARAM,
  ROLE_INTENT_PARAM,
  buildAuthCompletePath,
  readAuthHandoffUrl,
  resolveSatelliteAuthCompleteUrl,
  sanitizeOAuthCompletionUrl,
  sanitizeReturnTo,
} from '~/routing/returnTo';
import { paths } from '~/routing/routes';
import {
  readSessionTaskCompletionTarget,
  rememberSessionTaskCompletionTarget,
} from '~/routing/sessionTaskHandoff';
import { AuthLoading } from './AuthLoading';
import { SatelliteAuthUnavailable } from './SatelliteAuthUnavailable';
import { useSatelliteRedirect } from './useSatelliteRedirect';

// [MCV-1762] See SignInPage: a provider gives their name on the registration step, not here, and
// only the name row is hidden so the optional phone number field (MCV-1429) stays.
const HIDE_NAME_ROW_APPEARANCE = { elements: { formFieldRow__name: { display: 'none' } } };

interface SignUpPageProps {
  path?: string;
  returnTo?: string | null;
  /** Pre-fills Clerk's email field (the user can still change it). */
  initialEmail?: string | null;
}

/** Authentication creates a Clerk identity; the role gate creates the app account. */
export const SignUpPage = ({
  path = paths.publicAuth.signUp,
  returnTo,
  initialEmail,
}: SignUpPageProps) => {
  const { redirecting, blocked } = useSatelliteRedirect({ returnTo });
  const location = useLocation();
  const isManagedContinuation = location.pathname.replace(/\/+$/, '').startsWith(`${path}/`);
  const requestedReturnTo = sanitizeReturnTo(
    new URLSearchParams(location.search).get(RETURN_TO_PARAM) ?? returnTo,
    window.location.origin
  );
  const satelliteTarget = resolveSatelliteAuthCompleteUrl(
    new URLSearchParams(location.search).get(CLERK_SATELLITE_URL_PARAM)
  );
  const fallbackTarget = satelliteTarget ?? buildAuthCompletePath(requestedReturnTo);
  const authHandoff = sanitizeOAuthCompletionUrl(
    readAuthHandoffUrl(location.search, location.hash),
    window.location.origin
  );
  const completionTarget =
    authHandoff ??
    (isManagedContinuation ? readSessionTaskCompletionTarget() : null) ??
    fallbackTarget;
  const signInTarget = isManagedContinuation
    ? completionTarget
    : (authHandoff ?? satelliteTarget ?? (requestedReturnTo ? fallbackTarget : null));
  const signInUrl = signInTarget
    ? `${paths.publicAuth.signIn}?${new URLSearchParams({
        [CLERK_REDIRECT_URL_PARAM]: signInTarget,
      })}`
    : paths.publicAuth.signIn;

  // [MCV-1666] The combined sign-in card forwards the provider/organization
  // choice as ?role=. Honor it here so a new account created via this page
  // still auto-continues on RoleSelectPage; a fresh visit WITHOUT it is a
  // direct/stale signup and must clear any leftover intent. Clerk substeps
  // (nested path or hash) are mid-flow and leave storage untouched.
  const roleIntent = roleFromQueryValue(
    new URLSearchParams(location.search).get(ROLE_INTENT_PARAM)
  );
  const isFreshRootEntry = !isManagedContinuation && !hasNestedClerkHash(location.hash);
  useEffect(() => {
    if (roleIntent) {
      setPendingRole(roleIntent);
    } else if (isFreshRootEntry) {
      clearPendingRole();
    }
  }, [roleIntent, isFreshRootEntry]);

  // [MCV-1762] A Clerk sub-step (`/sign-up/continue`) drops `?role=`, so it reads the choice back
  // from storage. A fresh root entry must not: the effect above only clears a stale choice after
  // this render, so reading storage here would let it hide the names.
  const isProviderFlow =
    roleIntent === 'PROVIDER' ||
    path === paths.publicAuth.signUpProvider ||
    (!isFreshRootEntry && peekPendingRole() === 'PROVIDER');

  useEffect(() => {
    // A fresh signup starts a new flow; only Clerk substeps reuse its saved destination.
    rememberSessionTaskCompletionTarget(completionTarget);
  }, [completionTarget]);

  if (blocked) return <SatelliteAuthUnavailable />;
  if (redirecting) return <AuthLoading message="Connecting you to secure authentication…" />;

  return (
    <div className="flex min-h-screen items-center justify-center bg-primary-t-950">
      <SignUp
        routing="path"
        path={path}
        appearance={isProviderFlow ? HIDE_NAME_ROW_APPEARANCE : undefined}
        initialValues={initialEmail ? { emailAddress: initialEmail } : undefined}
        signInUrl={signInUrl}
        fallbackRedirectUrl={fallbackTarget}
        signInFallbackRedirectUrl={isManagedContinuation ? completionTarget : fallbackTarget}
        signInForceRedirectUrl={isManagedContinuation ? completionTarget : undefined}
        forceRedirectUrl={isManagedContinuation ? completionTarget : undefined}
      />
    </div>
  );
};
